import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';

export type CctvStreamStatus = 'loading' | 'live' | 'reconnecting' | 'error';

interface UseCctvStreamParams {
  streamUrl?: string | null;
  isHighRes?: boolean;
  autoPlay?: boolean;
}

// Connects a <video> element to a stream via hls.js, with the retry/reconnect
// behavior the edge stream needs (network hiccup -> reload, media error ->
// recover, fatal -> surface as 'error'). `streamUrl` is used as-is - the
// backend has already resolved it, so there's no client-side URL computation.
export function useCctvStream({ streamUrl = null, isHighRes = false, autoPlay = true }: UseCctvStreamParams) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<CctvStreamStatus>('loading');

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !streamUrl) {
      return;
    }

    setStatus('loading');
    let hls: Hls | undefined;
    const cleanupHandlers: Array<() => void> = [];

    if (Hls.isSupported()) {
      hls = new Hls({
        liveSyncDuration: 3,
        maxLiveSyncPlaybackRate: 1.5,
        manifestLoadingMaxRetry: isHighRes ? 15 : 3,
        manifestLoadingRetryDelay: 2000,
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setStatus('live');
        if (autoPlay) videoElement.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal || !hls) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          setStatus('reconnecting');
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          setStatus('error');
          hls.destroy();
        }
      });

      hls.loadSource(streamUrl);
      hls.attachMedia(videoElement);

      // hls.js only re-fires MANIFEST_PARSED on the very first load, not on
      // every internal reconnect (e.g. after the edge/warboy restarts and
      // hls.startLoad() above just resumes fragment loading without
      // re-parsing the manifest) - so 'reconnecting' could get stuck even
      // once the stream is visibly playing again. Clear it off the actual
      // <video> element's own 'playing' event instead, which fires whenever
      // playback (re)starts regardless of which hls.js path got it there.
      const onPlaying = () => setStatus('live');
      videoElement.addEventListener('playing', onPlaying);
      cleanupHandlers.push(() => videoElement.removeEventListener('playing', onPlaying));
    } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      videoElement.src = streamUrl;
      const onLoadedMetadata = () => {
        setStatus('live');
        if (autoPlay) videoElement.play().catch(() => {});
      };
      const onVideoError = () => setStatus('error');
      videoElement.addEventListener('loadedmetadata', onLoadedMetadata);
      videoElement.addEventListener('error', onVideoError);
      cleanupHandlers.push(() => {
        videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
        videoElement.removeEventListener('error', onVideoError);
      });
    } else {
      setStatus('error');
    }

    return () => {
      cleanupHandlers.forEach((cleanup) => cleanup());
      hls?.destroy();
    };
  }, [streamUrl, isHighRes, autoPlay]);

  return { videoRef, status };
}
